import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { F, mr } from '../styles/theme';
import { RedWordmark, RedSymbol } from '../components/red/Brand';
import { useT } from '../i18n/useT';
import { useProfile } from '../context/ProfileContext';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function AppleIcon({ color = 'currentColor' }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill={color}>
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.4c1.37.07 2.32.75 3.13.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.39-1.32 2.76-2.58 3.99zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  );
}

const FIREBASE_ERROR_KEYS = {
  'auth/user-not-found': 'auth_err_user_not_found',
  'auth/wrong-password': 'auth_err_wrong_password',
  'auth/invalid-credential': 'auth_err_invalid_credential',
  'auth/email-already-in-use': 'auth_err_email_in_use',
  'auth/weak-password': 'auth_err_weak_password',
  'auth/invalid-email': 'auth_err_invalid_email',
  'auth/too-many-requests': 'auth_err_too_many_requests',
  'auth/popup-closed-by-user': null,
};

export default function LoginScreen() {
  const { T } = useTheme();
  const { t, lang } = useT();
  const { setLanguage } = useProfile();
  const { signInWithGoogle, signInWithApple, signInWithEmail, signUpWithEmail, resetPassword, redirectError } = useAuth();

  const [mode, setMode] = useState('main'); // 'main' | 'login' | 'register' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const handleError = (e) => {
    const key = FIREBASE_ERROR_KEYS[e.code];
    // `null` entry = silent (e.g. user closed Google popup) → no message.
    if (key !== null) {
      let msg = key ? t(key) : t('auth_err_generic');
      // Bei NICHT gemappten Fehlern den Rohcode/Message anzeigen — sonst ist
      // „Fehler beim Anmelden" undiagnostizierbar (v.a. nativer Google-Login,
      // wo keine Konsole verfügbar ist).
      if (!key) {
        const detail = e?.code || e?.message;
        if (detail) msg += ' [' + String(detail).slice(0, 80) + ']';
      }
      setError(msg);
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setLoading(true); setError('');
    try { await signInWithGoogle(); } catch (e) { handleError(e); } finally { setLoading(false); }
  };

  const handleApple = async () => {
    setLoading(true); setError('');
    try { await signInWithApple(); } catch (e) { handleError(e); } finally { setLoading(false); }
  };

  const handleEmailLogin = async () => {
    if (!email || !password) return;
    setLoading(true); setError('');
    try { await signInWithEmail(email, password); } catch (e) { handleError(e); }
  };

  const handleRegister = async () => {
    if (!email || !password) return;
    setLoading(true); setError('');
    try { await signUpWithEmail(email, password); } catch (e) { handleError(e); }
  };

  const handleReset = async () => {
    if (!email) { setError(t('auth_err_enter_email')); return; }
    setLoading(true); setError('');
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch (e) { handleError(e); }
    setLoading(false);
  };

  // Editorial: scharfe 3px Inputs/Buttons, solid signal CTA, kein Glass.
  const inputStyle = {
    width: '100%', padding: '13px 16px', boxSizing: 'border-box',
    background: T.surfaceHi, border: `1px solid ${T.line}`, borderRadius: mr(16),
    fontSize: 15, color: T.text, fontFamily: F.family, outline: 'none',
  };

  const btnPrimary = {
    width: '100%', padding: '14px', background: T.signal || T.red,
    border: 'none', borderRadius: mr(16), fontSize: 15, fontWeight: 700,
    color: '#fff', fontFamily: F.family, cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.6 : 1, letterSpacing: '-0.01em',
  };

  const btnGlass = {
    width: '100%', padding: '14px', boxSizing: 'border-box',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
    background: 'transparent',
    border: `1.5px solid ${T.line}`, borderRadius: mr(16),
    fontSize: 15, fontWeight: 700, color: T.text, fontFamily: F.family,
    cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
    boxShadow: 'none',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      background: T.bg, color: T.text, fontFamily: F.family,
    }}>
      {/* Editorial Split: linke Markenfläche (signal) ab ≥860px */}
      <style>{`
        @media (max-width: 860px) {
          .login-brand-panel { display: none !important; }
          .login-form-panel { width: 100% !important; }
        }
      `}</style>
      <div className="login-brand-panel" style={{
        flex: 1,
        background: T.signal || T.red,
        color: '#fff',
        padding: '56px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Dynamischer Hintergrund: 2 driftende Radial-Highlights + langsam rotierende Lichtstrahlen */}
        <span aria-hidden className="login-bg-glow login-bg-glow-a" />
        <span aria-hidden className="login-bg-glow login-bg-glow-b" />
        <span aria-hidden className="login-bg-rays" />
        <div style={{ position: 'relative', fontFamily: F.brand, fontSize: 22, fontWeight: 800, letterSpacing: '-0.04em' }}>
          <span style={{ color: '#fff' }}>red</span><span aria-hidden style={{ display: 'inline-block', width: 3.96, height: 3.96, marginLeft: 2, background: '#fff', borderRadius: 0.79, verticalAlign: 'baseline' }} />
        </div>
        <div style={{ position: 'relative' }}>
          <h1 className="login-hero" style={{
            fontFamily: F.family,
            fontSize: 'clamp(58px, 7.6vw, 108px)',
            fontWeight: 800, letterSpacing: '-0.075em', lineHeight: 0.88,
            margin: 0,
            display: 'flex', flexDirection: 'column',
            position: 'relative',
          }}>
            <span className="login-hero-line" style={{ '--i': 0 }}>
              Track it<span aria-hidden className="login-hero-square" />
            </span>
            <span className="login-hero-line" style={{ '--i': 1 }}>
              Grow it<span aria-hidden className="login-hero-square" />
            </span>
            <span className="login-hero-line" style={{ '--i': 2 }}>
              Send it<span aria-hidden className="login-hero-square" />
            </span>
          </h1>
          <p className="login-hero-sub" style={{
            fontSize: 16, opacity: 0.92, marginTop: 18, maxWidth: 420,
            lineHeight: 1.5, fontFamily: F.family,
          }}>
            {t('auth_hero_sub')}
          </p>
        </div>
        <style>{`
          .login-hero-line {
            display: inline-block;
            opacity: 0;
            transform: translateY(28px);
            animation: loginHeroIn 760ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
            animation-delay: calc(120ms * var(--i) + 120ms);
            will-change: transform, opacity;
          }
          .login-hero-square {
            display: inline-block;
            width: clamp(15px, 1.95vw, 28px);
            height: clamp(15px, 1.95vw, 28px);
            border-radius: clamp(2px, 0.32vw, 5px);
            background: #fff;
            margin-left: clamp(4px, 0.5vw, 8px);
            vertical-align: baseline;
          }
          .login-bg-glow {
            position: absolute;
            width: 70vw; height: 70vw;
            max-width: 900px; max-height: 900px;
            border-radius: 50%;
            pointer-events: none;
            filter: blur(80px);
            mix-blend-mode: screen;
            z-index: 0;
            will-change: transform;
          }
          .login-bg-glow-a {
            top: -20%; left: -15%;
            background: radial-gradient(circle, rgba(255,180,170,0.55) 0%, rgba(255,140,130,0.0) 65%);
            animation: loginGlowDriftA 18s ease-in-out infinite alternate;
          }
          .login-bg-glow-b {
            bottom: -25%; right: -20%;
            background: radial-gradient(circle, rgba(120,15,15,0.65) 0%, rgba(80,8,8,0.0) 65%);
            animation: loginGlowDriftB 22s ease-in-out infinite alternate;
          }
          .login-bg-rays {
            position: absolute;
            top: 50%; left: 50%;
            width: 180vmax; height: 180vmax;
            margin-top: -90vmax; margin-left: -90vmax;
            pointer-events: none;
            z-index: 1;
            background: conic-gradient(
              from 0deg at 50% 50%,
              transparent 0deg,
              rgba(255,255,255,0.10) 18deg,
              transparent 36deg,
              transparent 110deg,
              rgba(255,255,255,0.07) 130deg,
              transparent 150deg,
              transparent 220deg,
              rgba(255,255,255,0.09) 245deg,
              transparent 265deg,
              transparent 330deg,
              rgba(255,255,255,0.06) 350deg,
              transparent 360deg
            );
            mix-blend-mode: overlay;
            animation: loginRayRotate 70s linear infinite;
            will-change: transform;
          }
          @keyframes loginRayRotate {
            to { transform: rotate(360deg); }
          }
          @keyframes loginGlowDriftA {
            0%   { transform: translate(0, 0) scale(1); }
            50%  { transform: translate(8vw, 6vh) scale(1.12); }
            100% { transform: translate(14vw, -2vh) scale(0.96); }
          }
          @keyframes loginGlowDriftB {
            0%   { transform: translate(0, 0) scale(1); }
            50%  { transform: translate(-10vw, -8vh) scale(1.15); }
            100% { transform: translate(-4vw, 6vh) scale(0.94); }
          }
          .login-brand-panel > *:not(.login-bg-rays):not(.login-bg-glow) { position: relative; z-index: 2; }
          .login-hero-sub {
            opacity: 0;
            transform: translateY(14px);
            animation: loginHeroSubIn 700ms cubic-bezier(0.22, 1, 0.36, 1) 900ms forwards;
          }
          @keyframes loginHeroIn {
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes loginHeroSubIn {
            to { opacity: 0.92; transform: translateY(0); }
          }
          @media (prefers-reduced-motion: reduce) {
            .login-hero-line, .login-hero-sub {
              opacity: 1 !important;
              transform: none !important;
              filter: none !important;
              animation: none !important;
            }
            .login-bg-rays, .login-bg-glow { animation: none !important; }
          }
        `}</style>
        <div style={{
          fontFamily: F.family, fontSize: 11.5, opacity: 0.8, letterSpacing: '0.12em',
          textTransform: 'uppercase', fontWeight: 700, position: 'relative',
        }}>
          <span style={{ color: '#fff' }}>red</span><span style={{ color: '#fff' }}>.</span>
          <span style={{ color: '#fff' }}> · Made in Germany</span>
        </div>
      </div>

      {/* Form-Panel rechts (oder Vollbild auf Mobile) */}
      <div className="login-form-panel" style={{
        width: 'min(480px, 100%)', flexShrink: 0,
        padding: '32px clamp(20px,5vw,64px)',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        alignItems: 'center',
      }}><div style={{ width: '100%', maxWidth: 360 }}>

      {/* Logo: Symbol + Wordmark */}
      <div style={{ textAlign: 'center', marginBottom: mode === 'main' ? 48 : 32 }}>
        <div style={{
          display: 'inline-flex', flexDirection: 'column',
          alignItems: 'center', gap: mode === 'main' ? 14 : 10,
        }}>
          <RedSymbol size={mode === 'main' ? 56 : 40} />
          <RedWordmark size={mode === 'main' ? 32 : 24} />
        </div>
        {mode === 'main' && (
          <div style={{ fontSize: 14, color: T.sub, marginTop: 12, fontFamily: F.family }}>{t('tagline')}</div>
        )}
      </div>

      <div style={{ width: '100%', maxWidth: 320, margin: '0 auto' }}>

        {/* ── MAIN: Google + E-Mail Auswahl ── */}
        {mode === 'main' && (
          <>
            <button style={btnGlass} onClick={handleGoogle} disabled={loading}>
              {loading ? <span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⟳</span> : <GoogleIcon />}
              {t('auth_continue_google')}
            </button>

            <button style={{ ...btnGlass, marginTop: 10 }} onClick={handleApple} disabled={loading}>
              <AppleIcon color={T.text} />
              {t('auth_continue_apple')}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
              <div style={{ flex: 1, height: 1, background: T.glassBorder }} />
              <span style={{ fontSize: 12, color: T.muted }}>{t('auth_or')}</span>
              <div style={{ flex: 1, height: 1, background: T.glassBorder }} />
            </div>

            <button style={btnGlass} onClick={() => { setMode('login'); setError(''); }} disabled={loading}>
              ✉ {t('auth_continue_email')}
            </button>

            <div style={{ marginTop: 20, textAlign: 'center' }}>
              <button onClick={() => { setMode('register'); setError(''); }}
                style={{ background: 'none', border: 'none', color: T.muted, fontSize: 13, cursor: 'pointer', fontFamily: F.family }}>
                {t('auth_no_account')} <span style={{ color: T.red, fontWeight: 700 }}>{t('auth_register')}</span>
              </button>
            </div>
          </>
        )}

        {/* ── LOGIN mit E-Mail ── */}
        {mode === 'login' && (
          <>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 20 }}>{t('auth_login_title')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input style={inputStyle} type="email" placeholder={t('auth_email')} value={email}
                onChange={e => setEmail(e.target.value)} autoFocus />
              <input style={inputStyle} type="password" placeholder={t('auth_password')} value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleEmailLogin()} />
            </div>
            <button style={{ ...btnPrimary, marginTop: 14 }} onClick={handleEmailLogin} disabled={loading || !email || !password}>
              {loading ? t('auth_login_loading') : `${t('auth_login_title')} →`}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14 }}>
              <button onClick={() => { setMode('main'); setError(''); }}
                style={{ background: 'none', border: 'none', color: T.muted, fontSize: 13, cursor: 'pointer', fontFamily: F.family }}>
                ← {t('back')}
              </button>
              <button onClick={() => { setMode('reset'); setError(''); }}
                style={{ background: 'none', border: 'none', color: T.muted, fontSize: 13, cursor: 'pointer', fontFamily: F.family }}>
                {t('auth_forgot_password')}
              </button>
            </div>
          </>
        )}

        {/* ── REGISTRIEREN ── */}
        {mode === 'register' && (
          <>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 20 }}>{t('auth_register_title')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input style={inputStyle} type="email" placeholder={t('auth_email')} value={email}
                onChange={e => setEmail(e.target.value)} autoFocus />
              <input style={inputStyle} type="password" placeholder={t('auth_password_min')} value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRegister()} />
            </div>
            <button style={{ ...btnPrimary, marginTop: 14 }} onClick={handleRegister} disabled={loading || !email || !password}>
              {loading ? t('auth_register_loading') : `${t('auth_register_title')} →`}
            </button>
            <button onClick={() => { setMode('main'); setError(''); }}
              style={{ background: 'none', border: 'none', color: T.muted, fontSize: 13, cursor: 'pointer', fontFamily: F.family, marginTop: 14, display: 'block' }}>
              ← {t('back')}
            </button>
          </>
        )}

        {/* ── PASSWORT RESET ── */}
        {mode === 'reset' && (
          <>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 8 }}>{t('auth_reset_title')}</div>
            {resetSent ? (
              <>
                <div style={{ fontSize: 14, color: T.green, marginBottom: 20, lineHeight: 1.5 }}>
                  ✓ {t('auth_reset_sent')}
                </div>
                <button style={btnPrimary} onClick={() => { setMode('login'); setResetSent(false); }}>
                  {t('auth_back_to_login')}
                </button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, color: T.muted, marginBottom: 16 }}>
                  {t('auth_reset_sub')}
                </div>
                <input style={inputStyle} type="email" placeholder={t('auth_email')} value={email}
                  onChange={e => setEmail(e.target.value)} autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleReset()} />
                <button style={{ ...btnPrimary, marginTop: 14 }} onClick={handleReset} disabled={loading || !email}>
                  {loading ? t('auth_reset_loading') : `${t('auth_reset_cta')} →`}
                </button>
                <button onClick={() => { setMode('login'); setError(''); }}
                  style={{ background: 'none', border: 'none', color: T.muted, fontSize: 13, cursor: 'pointer', fontFamily: F.family, marginTop: 14, display: 'block' }}>
                  ← {t('back')}
                </button>
              </>
            )}
          </>
        )}

        {error && (
          <div style={{ marginTop: 12, fontSize: 13, color: T.red, textAlign: 'center', lineHeight: 1.5 }}>
            {error}
          </div>
        )}

        {/* Redirect-Diagnose: macht sichtbar, warum „Mit Google anmelden" ohne
            Sitzung zurückkam (statt still auf der Login-Maske zu landen). */}
        {redirectError && !error && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: T.muted, textAlign: 'center', lineHeight: 1.5 }}>
            {redirectError === 'empty'
              ? 'Die Google-Anmeldung kam ohne Sitzung zurück. Tipp: im normalen Safari (nicht der Home-Bildschirm-App) anmelden.'
              : `Google-Login-Hinweis: ${redirectError}`}
          </div>
        )}
      </div>

      {mode === 'main' && (
        <>
          <div style={{ margin: '28px auto 0', fontSize: 12, color: T.muted, textAlign: 'center', maxWidth: 260, lineHeight: 1.5 }}>
            {t('auth_privacy_note')}
          </div>
          <div style={{ marginTop: 14, fontSize: 11, color: T.muted, textAlign: 'center', lineHeight: 1.6 }}>
            {t('auth_consent_pre')}{' '}
            <a href="https://redcrypto.de/agb" target="_blank" rel="noopener noreferrer"
               style={{ color: T.muted, textDecoration: 'underline' }}>AGB</a>
            {' '}{t('auth_consent_and')}{' '}
            <a href="https://redcrypto.de/datenschutz" target="_blank" rel="noopener noreferrer"
               style={{ color: T.muted, textDecoration: 'underline' }}>Datenschutzerklärung</a>.
            <br />
            <a href="https://redcrypto.de/impressum" target="_blank" rel="noopener noreferrer"
               style={{ color: T.muted, textDecoration: 'underline' }}>Impressum</a>
          </div>
        </>
      )}

      {/* Sprach-Umschalter — gleicher Slide-Thumb-Stil wie der Hero-TimeRail
          (gleitender Hintergrund statt rotem Unterstrich). */}
      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
        <div style={{
          position: 'relative', display: 'inline-flex',
          background: T.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(20,19,15,0.05)',
          borderRadius: 12, padding: 3,
        }}>
          <div style={{
            position: 'absolute', top: 3, bottom: 3,
            left: (lang || 'de').toLowerCase() === 'en' ? 'calc(50%)' : '3px',
            width: 'calc(50% - 3px)', borderRadius: 9,
            background: T.isDark ? 'rgba(255,255,255,0.12)' : 'rgba(20,19,15,0.08)',
            transition: 'left 240ms cubic-bezier(0.16,1,0.3,1)', pointerEvents: 'none',
          }} />
          {['de', 'en'].map((lc) => {
            const active = (lang || 'de').toLowerCase() === lc;
            return (
              <button key={lc} onClick={() => setLanguage(lc)}
                aria-label={lc === 'de' ? 'Deutsch' : 'English'} aria-pressed={active}
                style={{
                  position: 'relative', zIndex: 1,
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: F.family, fontSize: 12.5, fontWeight: active ? 800 : 600,
                  letterSpacing: '0.04em', color: active ? T.text : T.muted,
                  padding: '7px 22px', transition: 'color 200ms ease',
                  WebkitTapHighlightColor: 'transparent',
                }}>
                {lc.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>{/* /max-width form */}
      </div>{/* /login-form-panel */}
    </div>
  );
}
